import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Headphones, Search, Store, CreditCard, ShoppingBag, Bell, MessageCircle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { searchShops360, formatFcfa, type Shop360 } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA Call-center 360 (Epic 5.3) — single-pane shop lookup for the call
// center: search the book by name / owner / phone, then see identity, credit
// account state, order history rollups and open intelligence actions. Reads
// v_shop_360, which inherits shops RLS (call-center/finance/direction/ops see
// the whole book; an agent sees only theirs).
// ---------------------------------------------------------------------------

const CREDIT_STAGE_LABELS: Record<string, string> = {
  cash_only: "Comptant uniquement",
  credit_24h: "Crédit 24h",
  credit_48h: "Crédit 48h",
  credit_7d: "Crédit 7 jours",
  reduced: "Crédit réduit",
  frozen: "Gelé",
};

const CREDIT_STAGE_TONE: Record<string, string> = {
  cash_only: "bg-stone-100 text-stone-600",
  credit_24h: "bg-emerald-100 text-emerald-700",
  credit_48h: "bg-emerald-100 text-emerald-700",
  credit_7d: "bg-amber-100 text-amber-700",
  reduced: "bg-amber-100 text-amber-800",
  frozen: "bg-rose-100 text-rose-700",
};

function DetailStat({
  icon,
  label,
  value,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3">
      <div className="mb-1 flex items-center gap-2 text-stone-400">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className="text-lg font-semibold text-stone-900">{value}</p>
    </div>
  );
}

function Shop360Detail({ shop }: { readonly shop: Shop360 }) {
  const lastOrder = shop.last_order_at
    ? new Date(shop.last_order_at).toLocaleDateString("fr-FR")
    : "Jamais";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
          <Store className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-stone-900">{shop.name}</h2>
          <p className="text-sm text-stone-500">
            {shop.owner_name} · {shop.phone} · {shop.region} / {shop.zone}
          </p>
        </div>
        <span className={`ml-auto rounded-full px-3 py-1 text-xs font-medium ${CREDIT_STAGE_TONE[shop.credit_stage] ?? "bg-stone-100 text-stone-600"}`}>
          {CREDIT_STAGE_LABELS[shop.credit_stage] ?? shop.credit_stage}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <DetailStat icon={<CreditCard className="h-4 w-4" />} label="Plafond crédit" value={formatFcfa(shop.limit_xof)} />
        <DetailStat icon={<CreditCard className="h-4 w-4" />} label="Encours" value={formatFcfa(shop.exposure_xof)} />
        <DetailStat icon={<ShoppingBag className="h-4 w-4" />} label="Commandes" value={shop.order_count} />
        <DetailStat icon={<Bell className="h-4 w-4" />} label="Actions ouvertes" value={shop.open_action_count} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <DetailStat icon={<ShoppingBag className="h-4 w-4" />} label="Dernière commande" value={lastOrder} />
        <DetailStat icon={<Store className="h-4 w-4" />} label="Type" value={shop.shop_type} />
        <DetailStat icon={<Store className="h-4 w-4" />} label="Niveau" value={shop.tier} />
        <DetailStat
          icon={<MessageCircle className="h-4 w-4" />}
          label="WhatsApp"
          value={shop.whatsapp_opt_in ? "Opt-in ✓" : "Non"}
        />
      </div>
    </div>
  );
}

export default function CallCenter() {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const shops = useQuery({
    queryKey: ["djayma", "shop-360", query],
    queryFn: () => searchShops360(query),
    retry: false,
  });

  const results = shops.data ?? [];
  const selected = results.find((s) => s.shop_id === selectedId) ?? results[0] ?? null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Headphones className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Centre d&apos;appels — Fiche 360</h1>
            <p className="text-sm text-stone-500">
              Recherchez une boutique par nom, propriétaire ou téléphone
            </p>
          </div>
        </div>

        {/* Search */}
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSelectedId(null);
            setQuery(input);
          }}
        >
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Nom, propriétaire ou +221…"
              className="w-full rounded-lg border border-stone-200 bg-white py-2 pl-9 pr-3 text-sm text-stone-800 outline-none focus:border-amber-400"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
          >
            Rechercher
          </button>
        </form>

        {shops.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Fiche boutique non joignable — vérifiez que le schéma
            <code className="mx-1 rounded bg-amber-100 px-1">djayma</code> est exposé et que votre rôle y a accès.
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Results list */}
          <div className="rounded-xl border border-stone-200 bg-white p-2 shadow-sm lg:col-span-1">
            {results.length === 0 ? (
              <p className="p-3 text-sm text-stone-400">
                {shops.isLoading ? "Recherche…" : "Aucune boutique."}
              </p>
            ) : (
              <ul className="divide-y divide-stone-100">
                {results.map((s) => (
                  <li key={s.shop_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(s.shop_id)}
                      className={`w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-stone-50 ${
                        selected?.shop_id === s.shop_id ? "bg-amber-50" : ""
                      }`}
                    >
                      <p className="font-medium text-stone-800">{s.name}</p>
                      <p className="text-xs text-stone-400">{s.owner_name} · {s.phone}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail */}
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm lg:col-span-2">
            {selected ? (
              <Shop360Detail shop={selected} />
            ) : (
              <p className="text-sm text-stone-400">Sélectionnez une boutique pour afficher sa fiche 360.</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
